import { ref, computed, watch, type Ref, type ComputedRef, onUnmounted } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { StoreGeneric } from 'pinia'
import { type CRUDStore, type Filters } from '@cplug/frontend-core/stores'
import { useObjectMatches } from '@cplug/frontend-core/composables'
import { uniqueBy } from './useArrayUnique'

export interface UsePaginatedSearchOptions<Model extends object> {
  modelStoreInstance: CRUDStore<Model> | StoreGeneric
  itemValue: keyof Model
  itemLabel: keyof Model
  itemDescription?: (model: Model) => string
  perPage?: number
  filters?: Filters
  customFilters?: Filters
  persistent?: boolean
  searchField?: string
  debounceDelay?: number
  initialItem?: Model | null
  selectedItems?: Model | Model[] | null
}

export interface UsePaginatedSearchResult<Model extends object> {
  // Main states
  expanded: Ref<boolean>
  loading: Ref<boolean>
  firstLoadDone: Ref<boolean>
  searchText: Ref<string | undefined>

  // Data
  items: Ref<any[]>
  currentItems: ComputedRef<any[]>

  hasNextPage: ComputedRef<boolean>
  isFirstPage: ComputedRef<boolean>

  // Main methods
  onBeforeOpen: () => void
  onOpen: () => void
  onClose: () => void
  onFilter: (searchValue: string) => void
  onClear: () => void
  fetchItems: (callback?: () => void) => Promise<void>
  resetItems: (resetSearch?: boolean) => void

  // Utilities
  getOptionLabel: (option: Model) => string
  cleanUp: () => void
  updateSelectedItems: (value: Model | Model[] | null) => void
}

export function usePaginatedSearch<Model extends object> (
  options: UsePaginatedSearchOptions<Model>
): UsePaginatedSearchResult<Model> {
  const {
    modelStoreInstance: store,
    itemValue,
    itemLabel,
    perPage = 20,
    filters = {},
    customFilters = {},
    persistent = true, // Persist items during the open/close cycle
    searchField = 'search',
    debounceDelay = 500,
    initialItem = null,
    selectedItems = null
  } = options

  const reactiveSelectedItems = ref(selectedItems)

  const expanded = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const firstLoadDone = ref<boolean>(false)
  const searchText = ref<string>()
  const lastFetchedFilters = ref<Filters>()
  const lastFetchedSearchText = ref<string>()

  const items = ref<any[]>([])

  const debouncedSearch = useDebounceFn((searchValue: string) => {
    searchText.value = searchValue
  }, debounceDelay)

  const storePageMeta = computed(() => store.pageMeta)
  const storePageList = computed(() => store.pageList)

  const hasNextPage = computed(() => {
    const meta = storePageMeta.value
    return meta ? meta.page < meta.lastPage : false
  })

  const isFirstPage = computed(() => {
    const meta = storePageMeta.value
    return meta ? meta.page <= 1 : true
  })

  const persistItems = computed(() => persistent)

  const currentItems = computed(() => {
    // Durante o loading, ainda devemos mostrar os items existentes para o virtual scroll funcionar
    let currentItemList = expanded.value ? items.value : []

    // Automatically include items from selectedItems if they exist
    if (reactiveSelectedItems.value) {
      const selectedItems = Array.isArray(reactiveSelectedItems.value)
        ? reactiveSelectedItems.value
        : [reactiveSelectedItems.value]

      // Only include if they are complete objects (have itemValue and itemLabel)
      const validSelectedItems = selectedItems.filter(
        item => item && typeof item === 'object' && item[itemValue] != null && item[itemLabel]
      )

      currentItemList = [...validSelectedItems, ...currentItemList]
    }

    // Remove duplicates based on itemValue
    currentItemList = uniqueBy(currentItemList, itemValue)

    if (
      initialItem &&
      !currentItemList.find(item => (item as Model)[itemValue] === (initialItem as Model)[itemValue])
    ) {
      currentItemList = [initialItem as Model, ...currentItemList]
    }

    return currentItemList
  })

  const getOptionLabel = (option: Model): string => {
    return option[itemLabel] as string
  }

  const fetchItems = async (callback?: () => void): Promise<void> => {
    loading.value = true

    const searchValue = searchText.value
    const currentPageValue = !firstLoadDone.value ? 0 : storePageMeta.value?.page || 0
    const nextPage = currentPageValue + 1

    // Consider if the request is out of order or the component is no longer "active"
    const shouldSkipFetch = !expanded.value && !persistItems.value

    if (shouldSkipFetch && firstLoadDone.value) {
      // If the first load has already been done, and the skip condition is true, just exit.
      // This avoids displaying unnecessary loading if the request is cancelled internally.
      loading.value = false
      callback?.()

      return
    }

    try {
      const searchFilters = { ...filters, ...customFilters }
      if (searchValue) {
        searchFilters[searchField] = searchValue
      }

      await store.filter(searchFilters, nextPage, perPage, () => {
        const newItems = storePageList.value || []

        if (Array.isArray(newItems)) {
          items.value.push(...newItems)
        }

        lastFetchedFilters.value = searchFilters
        lastFetchedSearchText.value = searchValue

        if (isFirstPage.value) {
          firstLoadDone.value = true
        }
      })
    } catch (error) {
      firstLoadDone.value = false
    } finally {
      loading.value = false
      callback?.()
    }
  }

  const onBeforeOpen = (): void => {
    loading.value = true
    expanded.value = true
  }

  const onOpen = (): void => {
    // If the first page is not loaded or the first load has already been done, return
    if (!isFirstPage.value || firstLoadDone.value) {
      const searchFilters = { ...filters, ...customFilters }
      if (searchText.value) {
        searchFilters[searchField] = searchText.value
      }

      const currentFiltersAreSame = useObjectMatches(lastFetchedFilters.value, searchFilters)
      const currentSearchTextIsSame = lastFetchedSearchText.value === searchText.value

      if (items.value.length > 0 && currentFiltersAreSame && currentSearchTextIsSame) {
        // If we already have items for the current filters and search, don't make a new request
        loading.value = false
        return
      }
    }

    fetchItems()
  }

  const onClose = (): void => {
    expanded.value = false

    if (!persistItems.value) {
      resetItems(true)
      firstLoadDone.value = false
    }

    loading.value = false
  }

  const onFilter = (searchValue: string): void => {
    // Always trigger search if we don't have items or if search value changed
    const shouldSearch = searchValue !== searchText.value || items.value.length === 0

    if (shouldSearch) {
      loading.value = true
      expanded.value = true
      debouncedSearch(searchValue)
      return
    }

    expanded.value = true
    loading.value = false
  }

  const onClear = (): void => {
    onClose()
    searchText.value = undefined // Ensure the searchText is cleared when clearing the component
  }

  const resetItems = (resetSearch = false): void => {
    items.value = []

    if (resetSearch) {
      searchText.value = undefined
    }
  }

  const cleanUp = (): void => {
    expanded.value = false
    firstLoadDone.value = false
    loading.value = false
    searchText.value = undefined
    items.value = []
  }

  watch(searchText, () => {
    if (expanded.value) {
      firstLoadDone.value = false
      resetItems()
      fetchItems()
    } else if (searchText.value === '') {
      resetItems(true)
      firstLoadDone.value = false
    }
  })

  const updateSelectedItems = (value: Model | Model[] | null): void => {
    reactiveSelectedItems.value = value
  }

  onUnmounted(() => {
    cleanUp()
  })

  return {
    expanded,
    loading,
    firstLoadDone,
    searchText,
    items,
    currentItems,
    hasNextPage,
    isFirstPage,
    onBeforeOpen,
    onOpen,
    onClose,
    onFilter,
    onClear,
    fetchItems,
    resetItems,
    getOptionLabel,
    cleanUp,
    updateSelectedItems
  }
}
